import { applyPigmentDocumentMutation, canonicalPigmentJson, isPigmentJsonValue, } from "@pigment/core";
import { createPigmentAuthoringContext, } from "./authoring-context.js";
export class PigmentAuthoringEditError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.name = "PigmentAuthoringEditError";
        this.code = code;
    }
}
/**
 * Project the exact declared/current/default property vocabulary for one block.
 * This stays UI-neutral so browser consoles and agents do not each rebuild a
 * subtly different property model from the same manifest.
 */
export function pigmentAuthoringPropertyOptions(block) {
    if (!block?.propsPortable || !block.props)
        return [];
    const declared = Object.keys(block.capability?.props ?? {});
    const authoredOnly = Object.keys(block.props).filter((key) => !declared.includes(key)).sort();
    return [...declared, ...authoredOnly].map((key) => {
        const schema = block.capability?.props?.[key] ?? null;
        return {
            key,
            schema,
            current: block.props[key],
            hasCurrent: Object.hasOwn(block.props, key),
            defaultValue: schema?.default,
            hasDefault: schema ? Object.hasOwn(schema, "default") : false,
        };
    });
}
/** A human-editable source form which round-trips through the declared schema. */
export function pigmentAuthoringValueSource(value, schema) {
    if (value === undefined)
        return "";
    if (isTextKind(schema?.kind) && typeof value === "string")
        return value;
    return JSON.stringify(value, null, 2) ?? "";
}
/**
 * Parse a guided property field without pretending all values are JSON blobs.
 * Enum values are matched by exact portable identity; object/array/json fields
 * retain a scoped JSON editor while the complete desired document stays hidden
 * behind the caller's advanced surface.
 */
export function parsePigmentAuthoringPropertyValue(source, schema) {
    if (schema?.enum) {
        const match = schema.enum.find((candidate) => pigmentAuthoringValueSource(candidate, schema) === source);
        if (match !== undefined)
            return cloneJson(match);
        throw new PigmentAuthoringEditError("invalid_property_value", "Choose one of the host-declared property values.");
    }
    if (isTextKind(schema?.kind))
        return source;
    if (schema?.kind === "number") {
        const value = Number(source);
        if (source.trim() !== "" && Number.isFinite(value))
            return value;
        throw new PigmentAuthoringEditError("invalid_property_value", "Expected a finite number.");
    }
    if (schema?.kind === "boolean") {
        if (source === "true")
            return true;
        if (source === "false")
            return false;
        throw new PigmentAuthoringEditError("invalid_property_value", "Expected true or false.");
    }
    let value;
    try {
        value = JSON.parse(source);
    }
    catch (error) {
        throw new PigmentAuthoringEditError("invalid_property_value", `Expected portable JSON: ${error instanceof Error ? error.message : String(error)}`);
    }
    if (!isPigmentJsonValue(value) || !matchesKind(value, schema?.kind)) {
        throw new PigmentAuthoringEditError("invalid_property_value", `Expected ${schema?.kind ?? "portable JSON"}.`);
    }
    return value;
}
/**
 * Instantiate the host's canonical block example (or declared defaults) with a
 * caller-chosen stable root ID. Descendant example IDs are namespaced beneath
 * that root so repeated insertions cannot silently collide.
 */
export function instantiatePigmentAuthoringBlock(capability, stableId, exampleIndex = 0) {
    const id = stableId.trim();
    if (!id || id !== stableId) {
        throw new PigmentAuthoringEditError("invalid_block_id", "Block IDs must be non-empty trimmed text.");
    }
    if (!capability) {
        throw new PigmentAuthoringEditError("missing_block_capability", "Choose a host-declared block type.");
    }
    const example = capability.examples?.[exampleIndex];
    if (capability.examples?.length && !example) {
        throw new PigmentAuthoringEditError("missing_block_example", "Choose a host-declared block example.");
    }
    const declaredDefaults = defaultProps(capability);
    const base = example
        ? cloneBlock(example)
        : {
            id,
            type: capability.type,
            ...(declaredDefaults ? { props: declaredDefaults } : {}),
            ...(capability.allowedChildren || capability.slots ? { children: [] } : {}),
        };
    const namespace = (node, path) => ({
        ...node,
        id: path.length === 0 ? id : `${id}:${path.join(".")}`,
        ...(node.children ? { children: node.children.map((child, index) => namespace(child, [...path, index])) } : {}),
    });
    return namespace({ ...base, type: capability.type }, []);
}
/**
 * Enumerate every insertion index permitted by the exact manifest and current
 * tree. The append option for each destination retains its pre-0.9.0 ID and
 * semantics; earlier callers therefore keep appending while new callers can
 * choose a returned indexed option for deterministic insertion or reordering.
 *
 * The result is contextual and must be regenerated after each desired-document
 * edit. It intentionally excludes arbitrary parent/index text entry.
 */
export function pigmentAuthoringPlacementOptions(input) {
    // Re-project against the supplied desired document so a caller cannot pair a
    // stale block map with newer desired state and receive obsolete parents or
    // indices. The manifest remains the caller's exact host-declared contract.
    const context = createPigmentAuthoringContext(input.context.manifest, input.document);
    const moving = input.movingBlockId ? context.blocks.find((block) => block.id === input.movingBlockId) : null;
    if (input.movingBlockId && (!moving || moving.type !== input.blockType))
        return [];
    const capability = context.manifest.blocks.find((candidate) => candidate.type === input.blockType);
    if (!capability)
        return [];
    const excludedParents = input.movingBlockId ? descendantIds(input.document, input.movingBlockId) : new Set();
    const roots = writableRoots(input.document);
    const documentFingerprint = placementDocumentFingerprint(input.document);
    const options = [];
    for (const root of roots) {
        if (!moving || moving.slot === null) {
            const siblings = nodesForRoot(input.document, root);
            if (siblings) {
                options.push(...placements(root, null, siblings.length - (moving && sameRoot(moving.root, root) && moving.parentId === null ? 1 : 0), null, rootLabel(root), documentFingerprint));
            }
        }
        for (const parent of context.blocks.filter((block) => sameRoot(block.root, root) && block.known)) {
            if (excludedParents.has(parent.id))
                continue;
            const parentCapability = parent.capability;
            if (!parentCapability)
                continue;
            const childCount = childCountFor(input.document, parent.id, moving?.id);
            if (childCount === null)
                continue;
            if ((!moving || moving.slot === null) && allowsAnonymous(parentCapability, capability.type)) {
                options.push(...placements(root, parent.id, childCount, null, `Inside ${parent.capability?.label ?? parent.type} · ${parent.id}`, documentFingerprint));
            }
            for (const slot of parentCapability.slots ?? []) {
                if (moving && moving.slot !== slot.name)
                    continue;
                if (slot.allowedTypes && !slot.allowedTypes.includes(capability.type))
                    continue;
                const slotCount = slotChildCount(input.document, parent.id, slot.name, moving?.id);
                if (slotCount === null || (slot.maxChildren !== undefined && slotCount >= slot.maxChildren))
                    continue;
                options.push(...placements(root, parent.id, childCount, slot.name, `Inside ${parent.capability?.label ?? parent.type} · ${parent.id} · ${slot.name}`, documentFingerprint));
            }
        }
    }
    return options;
}
/** Validate one guided edit against current manifest/document state, then apply it through core without I/O. */
export function applyPigmentAuthoringGuidedEdit(context, document, edit) {
    const validatedEdit = validateStructuralEdit(context, document, edit);
    const operation = validatedEdit.op === "insert"
        ? {
            op: "insert",
            block: withSlot(validatedEdit.block, validatedEdit.placement.slot),
            placement: placementShape(validatedEdit.placement),
        }
        : validatedEdit.op === "move"
            ? { op: "move", id: validatedEdit.id, placement: placementShape(validatedEdit.placement) }
            : validatedEdit;
    const applied = applyPigmentDocumentMutation({ revision: 0, document }, { expectedRevision: 0, operations: [operation] });
    if (!applied.ok) {
        const detail = "message" in applied.conflict ? applied.conflict.message : applied.conflict.code;
        throw new PigmentAuthoringEditError(applied.conflict.code === "duplicate_block_id" ? "duplicate_block_id"
            : applied.conflict.code === "invalid_placement" || applied.conflict.code === "cycle" ? "invalid_placement"
                : "edit_conflict", `Guided edit could not be applied: ${detail}`);
    }
    return applied.state.document;
}
function validateStructuralEdit(context, document, edit) {
    if (edit.op !== "insert" && edit.op !== "move")
        return edit;
    const blockType = edit.op === "insert"
        ? edit.block.type
        : createPigmentAuthoringContext(context.manifest, document).blocks.find((block) => block.id === edit.id)?.type;
    if (!blockType) {
        throw new PigmentAuthoringEditError("invalid_placement", "Guided move target is absent from the current desired document.");
    }
    const legal = pigmentAuthoringPlacementOptions({
        context,
        document,
        blockType,
        ...(edit.op === "move" ? { movingBlockId: edit.id } : {}),
    });
    const placement = legal.find((candidate) => placementOptionMatches(candidate, edit.placement));
    if (!placement) {
        throw new PigmentAuthoringEditError("invalid_placement", "Choose a placement returned for the current desired document and host manifest.");
    }
    return { ...edit, placement };
}
function placementOptionMatches(candidate, selected) {
    if (selected.selectionKey)
        return candidate.selectionKey === selected.selectionKey;
    // Compatibility is deliberately append-only. Indexed positions must carry
    // the collision-free identity minted for the exact current document.
    return selected.mode !== "indexed"
        && candidate.mode === "append"
        && candidate.id === selected.id
        && candidate.index === selected.index
        && candidate.parentId === selected.parentId
        && candidate.slot === selected.slot
        && sameRoot(candidate.root, selected.root);
}
function defaultProps(capability) {
    const entries = Object.entries(capability.props ?? {}).flatMap(([key, schema]) => schema.default === undefined ? [] : [[key, cloneJson(schema.default)]]);
    return entries.length ? Object.fromEntries(entries) : null;
}
function writableRoots(document) {
    const roots = [{ kind: "blocks" }];
    for (const slot of ["announcement", "header", "footer"]) {
        if (document.chrome?.[slot]?.mode === "custom")
            roots.push({ kind: "chrome", slot });
    }
    return roots;
}
function nodesForRoot(document, root) {
    if (root.kind === "blocks")
        return document.blocks ?? [];
    return document.chrome?.[root.slot]?.mode === "custom" ? document.chrome[root.slot].blocks ?? [] : null;
}
function findBlock(nodes, id) {
    for (const node of nodes) {
        if (node.id === id)
            return node;
        const nested = findBlock(node.children ?? [], id);
        if (nested)
            return nested;
    }
    return null;
}
function findDocumentBlock(document, id) {
    for (const root of writableRoots(document)) {
        const found = findBlock(nodesForRoot(document, root) ?? [], id);
        if (found)
            return found;
    }
    return null;
}
function descendantIds(document, id) {
    const root = findDocumentBlock(document, id);
    const ids = new Set([id]);
    const visit = (node) => {
        ids.add(node.id);
        node.children?.forEach(visit);
    };
    root?.children?.forEach(visit);
    return ids;
}
function childCountFor(document, parentId, movingId) {
    const parent = findDocumentBlock(document, parentId);
    if (!parent)
        return null;
    return (parent.children ?? []).filter((child) => child.id !== movingId).length;
}
function slotChildCount(document, parentId, slot, movingId) {
    const parent = findDocumentBlock(document, parentId);
    if (!parent)
        return null;
    return (parent.children ?? []).filter((child) => child.id !== movingId && child.slot === slot).length;
}
function allowsAnonymous(parent, childType) {
    // A manifest must positively declare composition. Omission is treated as a
    // leaf by authoring surfaces even though core keeps accepting older,
    // unconstrained documents for compatibility.
    return parent.allowedChildren?.includes(childType) ?? false;
}
function placement(root, parentId, index, slot, label, mode, documentFingerprint) {
    const rootId = root.kind === "blocks" ? "blocks" : `chrome:${root.slot}`;
    const destinationId = `${rootId}:${parentId ?? "root"}:${slot ?? "children"}`;
    const selectionKey = `pigment-placement:v1:${encodeURIComponent(canonicalPigmentJson({
        root,
        parentId,
        index: Math.max(0, index),
        mode,
        slot,
        documentFingerprint,
    }))}`;
    return {
        id: mode === "append" ? destinationId : `${destinationId}:index:${index}`,
        selectionKey,
        label,
        root: cloneRoot(root),
        parentId,
        index: Math.max(0, index),
        mode,
        slot,
    };
}
function placements(root, parentId, appendIndex, slot, label, documentFingerprint) {
    const boundedAppendIndex = Math.max(0, appendIndex);
    const indexed = Array.from({ length: boundedAppendIndex }, (_, index) => placement(root, parentId, index, slot, `${label} · position ${index + 1} of ${boundedAppendIndex + 1}`, "indexed", documentFingerprint));
    // Keep the exact historical ID and concise label for append. This is both a
    // compatibility promise and the unambiguous default for simple callers.
    return [...indexed, placement(root, parentId, boundedAppendIndex, slot, label, "append", documentFingerprint)];
}
function placementDocumentFingerprint(document) {
    const structuralNode = (node) => ({
        id: node.id,
        type: node.type,
        slot: node.slot ?? null,
        children: (node.children ?? []).map(structuralNode),
    });
    const source = canonicalPigmentJson({
        blocks: (document.blocks ?? []).map(structuralNode),
        chrome: Object.fromEntries(["announcement", "header", "footer"].map((slot) => {
            const override = document.chrome?.[slot];
            return [slot, override?.mode === "custom"
                    ? { mode: "custom", blocks: (override.blocks ?? []).map(structuralNode) }
                    : { mode: override?.mode ?? "inherit" }];
        })),
    });
    // A compact synchronous identity keeps browser option values bounded. This
    // is not an authority hash: apply-time validation also re-enumerates and uses
    // the matched canonical option, so a caller-mutated tuple is never trusted.
    let left = 0x811c9dc5;
    let right = 0x9e3779b9;
    for (let index = 0; index < source.length; index += 1) {
        const code = source.charCodeAt(index);
        left = Math.imul(left ^ code, 0x01000193) >>> 0;
        right = Math.imul(right ^ code, 0x85ebca6b) >>> 0;
    }
    return `${source.length.toString(36)}-${left.toString(16).padStart(8, "0")}-${right.toString(16).padStart(8, "0")}`;
}
function rootLabel(root) {
    return root.kind === "blocks" ? "Page root" : `Chrome ${root.slot} root`;
}
function placementShape(option) {
    return { root: cloneRoot(option.root), parentId: option.parentId, index: option.index };
}
function withSlot(block, slot) {
    const next = cloneBlock(block);
    if (slot === null)
        delete next.slot;
    else
        next.slot = slot;
    return next;
}
function cloneRoot(root) {
    return root.kind === "blocks" ? { kind: "blocks" } : { kind: "chrome", slot: root.slot };
}
function sameRoot(left, right) {
    return left.kind === right.kind && (left.kind === "blocks" || (right.kind === "chrome" && left.slot === right.slot));
}
function cloneBlock(node) {
    return {
        ...node,
        ...(node.props ? { props: cloneJson(node.props) } : {}),
        ...(node.children ? { children: node.children.map(cloneBlock) } : {}),
    };
}
function cloneJson(value) {
    return structuredClone(value);
}
function isTextKind(kind) {
    return kind === "string" || kind === "richText" || kind === "url" || kind === "color" || kind === "id";
}
function matchesKind(value, kind) {
    if (!kind || kind === "json")
        return true;
    if (kind === "array")
        return Array.isArray(value);
    if (kind === "object")
        return typeof value === "object" && value !== null && !Array.isArray(value);
    if (kind === "number")
        return typeof value === "number";
    if (kind === "boolean")
        return typeof value === "boolean";
    return typeof value === "string";
}
